import { PageClassSelector } from '../../../components/layout/TeacherWorkspace';
import { studentPreviewHref } from '../../../services/studentPreview';
import React, { useEffect, useMemo, useState } from 'react';
import { preferredTeacherSection, rememberTeacherSection } from '../../../services/teacherSection';
import { Link, useNavigate } from 'react-router-dom';
import { sectionsApi } from '../../../services/apiV2';
import { Section } from '../../../types/v2';
import TeacherNav from '../../../components/TeacherNav';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { PageHeader } from '../../../components/layout/PageHeader';

interface Student {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  grade_level?: string;
  grade_level_at_enrollment?: string;
  student_id?: string;
  group_name?: string;
}

interface StudentPoolRow {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  grade_level?: string;
  student_id?: string;
  is_enrolled: boolean;
}

const GRADE_ORDER = ['6th', '7th', '8th', '9th', '10th', '11th', '12th'];
const GRADE_OPTIONS = ['', ...GRADE_ORDER];

type GroupMode = 'none' | 'grade' | 'custom';

export const V2Students: React.FC = () => {
  const navigate = useNavigate();
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_students');
  const [sections, setSections] = useState<Section[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  useEffect(() => { rememberTeacherSection(selectedSectionId); }, [selectedSectionId]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [showExistingModal, setShowExistingModal] = useState(false);
  const [newStudent, setNewStudent] = useState({
    first_name: '',
    last_name: '',
    email: '',
    grade_level: '',
    student_id: '',
  });

  const [poolLoading, setPoolLoading] = useState(false);
  const [poolSearch, setPoolSearch] = useState('');
  const [poolGradeLevel, setPoolGradeLevel] = useState('');
  const [studentPool, setStudentPool] = useState<StudentPoolRow[]>([]);
  const [selectedPoolIds, setSelectedPoolIds] = useState<number[]>([]);

  const [groupMode, setGroupMode] = useState<GroupMode>('none');
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [customGroupName, setCustomGroupName] = useState('');
  const [assigningGroup, setAssigningGroup] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentGradeFilter, setStudentGradeFilter] = useState('');
  const [updatingGradeStudentId, setUpdatingGradeStudentId] = useState<number | null>(null);

  const normalizeArray = (data: any) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  };

  const loadSections = async () => {
    const response = await sectionsApi.list();
    const sectionsData = normalizeArray(response.data).filter((section: Section) => section.is_active !== false);
    setSections(sectionsData);
    if (sectionsData.length > 0 && !selectedSectionId) {
      setSelectedSectionId(preferredTeacherSection(sectionsData));
    }
  };

  const loadStudents = async (sectionId: number) => {
    setLoading(true);
    try {
      const response = await sectionsApi.students(sectionId);
      setStudents(response.data?.students || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const loadStudentPool = async () => {
    if (!selectedSectionId) return;
    setPoolLoading(true);
    try {
      const response = await sectionsApi.studentPool(selectedSectionId, {
        q: poolSearch.trim() || undefined,
        grade_level: poolGradeLevel || undefined,
      });
      setStudentPool(response.data?.students || []);
      setSelectedPoolIds([]);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load student pool');
    } finally {
      setPoolLoading(false);
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  useEffect(() => {
    if (!selectedSectionId) return;
    setError('');
    setSuccess('');
    loadStudents(selectedSectionId);
  }, [selectedSectionId]);

  useEffect(() => {
    setSelectedStudentIds([]);
  }, [selectedSectionId]);

  const handleAddStudent = async () => {
    if (!selectedSectionId || !newStudent.email.trim()) return;
    setError('');
    setSuccess('');

    const esc = (value: string) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [
      'first_name,last_name,email,grade_level,student_id',
      [
        esc(newStudent.first_name.trim()),
        esc(newStudent.last_name.trim()),
        esc(newStudent.email.trim().toLowerCase()),
        esc(newStudent.grade_level.trim()),
        esc(newStudent.student_id.trim()),
      ].join(','),
    ].join('\n');

    try {
      const file = new File([csv], 'single-student.csv', { type: 'text/csv' });
      await sectionsApi.importStudents(selectedSectionId, file);
      setNewStudent({
        first_name: '',
        last_name: '',
        email: '',
        grade_level: '',
        student_id: '',
      });
      setShowAddModal(false);
      setSuccess('Student added.');
      await loadStudents(selectedSectionId);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to add student');
    }
  };

  const handleAddExistingStudents = async () => {
    if (!selectedSectionId || selectedPoolIds.length === 0) return;
    setError('');
    setSuccess('');
    try {
      await sectionsApi.enroll(selectedSectionId, selectedPoolIds);
      setSuccess(`${selectedPoolIds.length} existing student${selectedPoolIds.length === 1 ? '' : 's'} added.`);
      await Promise.all([loadStudents(selectedSectionId), loadStudentPool()]);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to add selected students');
    }
  };

  const handleRemoveStudent = async (studentId: number) => {
    if (!selectedSectionId) return;
    if (!window.confirm('Remove this student from the section?')) return;
    setError('');
    setSuccess('');

    try {
      await sectionsApi.withdraw(selectedSectionId, [studentId]);
      setSuccess('Student removed.');
      await loadStudents(selectedSectionId);
      setSelectedStudentIds((prev) => prev.filter((id) => id !== studentId));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to remove student');
    }
  };

  const handleGradeChange = async (student: Student, gradeLevel: string) => {
    const profileMatches = gradeLevel === student.grade_level;
    const enrollmentMatches = gradeLevel === student.grade_level_at_enrollment;
    if (!selectedSectionId || !GRADE_ORDER.includes(gradeLevel) || (profileMatches && enrollmentMatches)) return;
    setUpdatingGradeStudentId(student.id);
    setError('');
    setSuccess('');
    try {
      await sectionsApi.updateStudentGrade(selectedSectionId, student.id, gradeLevel);
      setStudents((current) => current.map((row) => (
        row.id === student.id
          ? { ...row, grade_level: gradeLevel, grade_level_at_enrollment: gradeLevel }
          : row
      )));
      setSuccess(`${student.first_name || 'Student'}'s grade updated across active classes.`);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update student grade');
    } finally {
      setUpdatingGradeStudentId(null);
    }
  };

  const handleImportCSV = () => {
    if (!selectedSectionId) return;
    navigate(`/v2/teacher/section/${selectedSectionId}`);
  };

  const toggleStudentSelection = (studentId: number) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  const togglePoolSelection = (studentId: number) => {
    setSelectedPoolIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  const handleAssignGroup = async (groupName: string) => {
    if (!selectedSectionId || selectedStudentIds.length === 0) return;
    setAssigningGroup(true);
    setError('');
    setSuccess('');
    try {
      await sectionsApi.assignGroups(selectedSectionId, selectedStudentIds, groupName.trim());
      setSuccess(groupName.trim() ? `Assigned group "${groupName.trim()}".` : 'Cleared custom group.');
      setCustomGroupName(groupName.trim() ? groupName : '');
      await loadStudents(selectedSectionId);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update student groups');
    } finally {
      setAssigningGroup(false);
    }
  };

  const filteredStudents = useMemo(() => {
    const query = studentSearch.trim().toLowerCase();
    return students.filter((student) => {
      if (studentGradeFilter && student.grade_level !== studentGradeFilter) return false;
      if (!query) return true;
      const haystack = [
        student.first_name,
        student.last_name,
        student.email,
        student.student_id,
        student.group_name,
        student.grade_level,
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [studentGradeFilter, studentSearch, students]);

  const sortedStudents = useMemo(() => {
    return [...filteredStudents].sort((a, b) => {
      const aLast = String(a.last_name || '').toLowerCase();
      const bLast = String(b.last_name || '').toLowerCase();
      if (aLast !== bLast) return aLast.localeCompare(bLast);
      const aFirst = String(a.first_name || '').toLowerCase();
      const bFirst = String(b.first_name || '').toLowerCase();
      return aFirst.localeCompare(bFirst);
    });
  }, [filteredStudents]);

  const groupedStudents = useMemo(() => {
    if (groupMode === 'none') {
      return [{ key: 'all', label: `All Students (${sortedStudents.length})`, students: sortedStudents }];
    }

    const groups = new Map<string, Student[]>();
    for (const student of sortedStudents) {
      const key = groupMode === 'grade'
        ? (student.grade_level || 'No Grade')
        : (student.group_name || 'Ungrouped');
      const list = groups.get(key) || [];
      list.push(student);
      groups.set(key, list);
    }

    const entries = Array.from(groups.entries());
    if (groupMode === 'grade') {
      const rank = (grade: string) => {
        const idx = GRADE_ORDER.indexOf(grade);
        return idx >= 0 ? idx : 999;
      };
      entries.sort((a, b) => {
        const rankDiff = rank(a[0]) - rank(b[0]);
        if (rankDiff !== 0) return rankDiff;
        return a[0].localeCompare(b[0]);
      });
    } else {
      entries.sort((a, b) => a[0].localeCompare(b[0]));
    }

    return entries.map(([key, list]) => ({ key, label: `${key} (${list.length})`, students: list }));
  }, [groupMode, sortedStudents]);

  const availablePool = useMemo(
    () => studentPool.filter((student) => !student.is_enrolled),
    [studentPool],
  );

  return (
    <OptionalAppShell enabled={redesignEnabled} role="teacher" navItems={teacherV2NavItems('students')}>
      {!redesignEnabled && <TeacherNav activeTab="students" />}
      <div className={redesignEnabled ? 'space-y-4' : 'max-w-6xl mx-auto p-6'}>
        {redesignEnabled ? (
          <PageHeader
            eyebrow="Teacher Workspace"
            title="Students"
            subtitle="Manage student enrollments by section"
            actions={(
              <Link
                className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white"
                to="/v2/teacher/rollover"
              >
                New year rollover
              </Link>
            )}
          />
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-gray-900">Students</h1>
            <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-500">Manage student enrollments by section</p>
              <Link className="rounded bg-indigo-600 px-4 py-2 text-sm text-white" to="/v2/teacher/rollover">
                New year rollover
              </Link>
            </div>
          </>
        )}

        {error && (
          <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="mt-4 rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {success}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-4 items-end">
          <PageClassSelector><div>
            <label className="text-sm text-gray-600">Section</label>
            <select
              className="mt-1 block w-56 border rounded px-3 py-2"
              value={selectedSectionId || ''}
              onChange={(e) => setSelectedSectionId(Number(e.target.value))}
            >
              {sections.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
            </select>
          </div></PageClassSelector>

          <div className="flex flex-wrap gap-2">
            <button
              className="px-4 py-2 bg-indigo-600 text-white rounded text-sm"
              onClick={() => setShowAddModal(true)}
            >
              Add Student
            </button>
            <button
              className="px-4 py-2 border border-indigo-300 text-indigo-700 rounded text-sm"
              onClick={async () => {
                setShowExistingModal(true);
                await loadStudentPool();
              }}
              disabled={!selectedSectionId}
            >
              Add Existing
            </button>
            <button
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm"
              onClick={handleImportCSV}
            >
              Import CSV
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-4 rounded-lg border border-gray-200 bg-white p-4">
          <div>
            <label className="block text-sm text-gray-600">Search students</label>
            <input
              className="mt-1 block w-64 border rounded px-3 py-2"
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              placeholder="Name, email, ID, group"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600">Grade filter</label>
            <select
              className="mt-1 block border rounded px-3 py-2"
              value={studentGradeFilter}
              onChange={(e) => setStudentGradeFilter(e.target.value)}
            >
              {GRADE_OPTIONS.map((grade) => (
                <option key={grade || 'all-grades'} value={grade}>
                  {grade || 'All grades'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600">Group display</label>
            <select
              className="mt-1 block border rounded px-3 py-2"
              value={groupMode}
              onChange={(e) => setGroupMode(e.target.value as GroupMode)}
            >
              <option value="none">No grouping</option>
              <option value="grade">Group by grade</option>
              <option value="custom">Group by custom name</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600">Custom group name</label>
            <input
              className="mt-1 block w-56 border rounded px-3 py-2"
              value={customGroupName}
              onChange={(e) => setCustomGroupName(e.target.value)}
              placeholder="e.g., 10th Grade 1B Team A"
            />
          </div>
          <button
            className="px-3 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-60"
            onClick={() => handleAssignGroup(customGroupName)}
            disabled={assigningGroup || selectedStudentIds.length === 0 || !customGroupName.trim()}
          >
            Name Selected Group
          </button>
          <button
            className="px-3 py-2 border border-gray-300 text-gray-700 rounded text-sm disabled:opacity-60"
            onClick={() => handleAssignGroup('')}
            disabled={assigningGroup || selectedStudentIds.length === 0}
          >
            Clear Group Name
          </button>
          <p className="text-sm text-gray-500">
            {selectedStudentIds.length} selected | {sortedStudents.length} shown
          </p>
        </div>

        {loading ? (
          <p className="mt-6 text-gray-500">Loading...</p>
        ) : students.length === 0 ? (
          <div className="mt-6 bg-white rounded-lg shadow p-6 text-center text-gray-500">
            No students enrolled in this section.
          </div>
        ) : sortedStudents.length === 0 ? (
          <div className="mt-6 bg-white rounded-lg shadow p-6 text-center text-gray-500">
            No students match the current filters.
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {groupedStudents.map((group) => (
              <div key={group.key} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-sm font-medium text-gray-700">{group.label}</p>
                  <button
                    className="text-xs text-indigo-600 hover:underline"
                    onClick={() => {
                      const ids = group.students.map((student) => student.id);
                      const everySelected = ids.every((id) => selectedStudentIds.includes(id));
                      if (everySelected) {
                        setSelectedStudentIds((prev) => prev.filter((id) => !ids.includes(id)));
                      } else {
                        setSelectedStudentIds((prev) => Array.from(new Set([...prev, ...ids])));
                      }
                    }}
                  >
                    {group.students.every((student) => selectedStudentIds.includes(student.id)) ? 'Clear Group Selection' : 'Select Group'}
                  </button>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Select</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Custom Group</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student ID</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {group.students.map((student) => (
                      <tr key={student.id}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedStudentIds.includes(student.id)}
                            onChange={() => toggleStudentSelection(student.id)}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <Link
                            to={`/v2/teacher/students/${student.id}/work${selectedSectionId ? `?section=${selectedSectionId}` : ''}`}
                            className="text-indigo-700 hover:text-indigo-900 hover:underline"
                          >
                            {student.first_name} {student.last_name}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <select
                            aria-label={`Grade for ${student.first_name} ${student.last_name}`}
                            className="rounded border border-gray-300 px-2 py-1 text-sm"
                            value={student.grade_level_at_enrollment || student.grade_level || ''}
                            onChange={(event) => handleGradeChange(student, event.target.value)}
                            disabled={updatingGradeStudentId === student.id}
                          >
                            <option value="" disabled>Not set</option>
                            {GRADE_ORDER.map((grade) => <option key={grade} value={grade}>{grade}</option>)}
                          </select>
                          {student.grade_level
                            && student.grade_level_at_enrollment
                            && student.grade_level !== student.grade_level_at_enrollment && (
                              <span className="ml-2 text-xs text-amber-700">
                                Profile: {student.grade_level}
                              </span>
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.group_name || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.student_id || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {selectedSectionId && <a href={studentPreviewHref(selectedSectionId, student.id)} target="_blank" rel="noopener noreferrer" className="mr-4 text-indigo-700 hover:underline" aria-label={`View as student: ${student.first_name} ${student.last_name} (opens in a new tab)`}>View as student</a>}
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={() => handleRemoveStudent(student.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96 max-w-full">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Student</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border rounded px-3 py-2"
                    value={newStudent.first_name}
                    onChange={(e) => setNewStudent({ ...newStudent, first_name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border rounded px-3 py-2"
                    value={newStudent.last_name}
                    onChange={(e) => setNewStudent({ ...newStudent, last_name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email *</label>
                  <input
                    type="email"
                    className="mt-1 block w-full border rounded px-3 py-2"
                    value={newStudent.email}
                    onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Grade Level</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border rounded px-3 py-2"
                    value={newStudent.grade_level}
                    onChange={(e) => setNewStudent({ ...newStudent, grade_level: e.target.value })}
                    placeholder="e.g., 9th"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Student ID</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border rounded px-3 py-2"
                    value={newStudent.student_id}
                    onChange={(e) => setNewStudent({ ...newStudent, student_id: e.target.value })}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-indigo-600 text-white rounded text-sm"
                  onClick={handleAddStudent}
                  disabled={!newStudent.email.trim()}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {showExistingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-[760px] max-w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Existing Students</h2>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-2">
                <input
                  className="border rounded px-3 py-2"
                  placeholder="Search by name, email, or student ID"
                  value={poolSearch}
                  onChange={(e) => setPoolSearch(e.target.value)}
                />
                <select
                  className="border rounded px-3 py-2"
                  value={poolGradeLevel}
                  onChange={(e) => setPoolGradeLevel(e.target.value)}
                >
                  {GRADE_OPTIONS.map((grade) => (
                    <option key={grade || 'all'} value={grade}>
                      {grade || 'All grades'}
                    </option>
                  ))}
                </select>
                <button
                  className="px-3 py-2 border border-gray-300 rounded text-sm"
                  onClick={loadStudentPool}
                  disabled={poolLoading}
                >
                  {poolLoading ? 'Searching...' : 'Search'}
                </button>
              </div>

              <div className="mt-4 rounded border border-gray-200">
                {poolLoading ? (
                  <p className="p-4 text-sm text-gray-500">Loading student pool...</p>
                ) : availablePool.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500">No available students found.</p>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Select</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student ID</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {availablePool.map((student) => (
                        <tr key={student.id}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedPoolIds.includes(student.id)}
                              onChange={() => togglePoolSelection(student.id)}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{student.first_name} {student.last_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{student.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{student.grade_level || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{student.student_id || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm"
                  onClick={() => {
                    setShowExistingModal(false);
                    setSelectedPoolIds([]);
                  }}
                >
                  Close
                </button>
                <button
                  className="px-4 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-60"
                  onClick={handleAddExistingStudents}
                  disabled={selectedPoolIds.length === 0}
                >
                  Add Selected ({selectedPoolIds.length})
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </OptionalAppShell>
  );
};

export default V2Students;
